import React, { useEffect, useRef, useState } from "react";
import { Plus, Building2, Users, Sparkles, ArrowUpRight, Edit3, Trash2, ExternalLink, Palette, Upload, X as XIcon, Image as ImageIcon, Percent, LogIn, Eye, Stethoscope } from "lucide-react";
import { api, formatGBP, formatNumber, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { TopBar, KpiCard, FooterStrip, SouburWordmark, Modal, Field, TextInput, PrimaryButton, GhostButton, Pill, Crest } from "../../components/shell";

const COLOR_PRESETS = [
    { name: "Floodlight Amber (default)", primary: "#F59E0B", primary_foreground: "#000000" },
    { name: "Pitch Green", primary: "#166534", primary_foreground: "#FEF08A" },
    { name: "Claret", primary: "#7F1D1D", primary_foreground: "#FFE4E6" },
    { name: "Royal Blue", primary: "#1E3A8A", primary_foreground: "#FFFFFF" },
    { name: "Sky Blue", primary: "#0EA5E9", primary_foreground: "#0F172A" },
    { name: "Black & Gold", primary: "#0a0a0a", primary_foreground: "#F5C518" },
];

const emptyClub = () => ({
    slug: "",
    name: "",
    short_name: "",
    league: "",
    venue: "",
    founded_year: "",
    season: "2025/26",
    subdomain: "",
    pricing_mode: "commission",
    commission_pct: 15,
    transaction_fees_absorbed: true,
    pricing_monthly_gbp: 0,
    stripe_account_id: "",
    stripe_metadata_key: "club",
    stripe_metadata_value: "",
    stripe_metadata_tier_key: "tier",
    payout_schedule: "",
    subscriber_tiers: [],
    brand: { primary: "#166534", primary_foreground: "#FEF08A", secondary: "#FEF08A", crest_initials: "FC", tagline: "", logo_path: null },
});

export default function PlatformOwner() {
    const { user } = useAuth();
    const [tenants, setTenants] = useState([]);
    const [overview, setOverview] = useState(null);
    const [integrations, setIntegrations] = useState(null);
    const [diagnosticOpen, setDiagnosticOpen] = useState(null); // tenant object
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [members, setMembers] = useState({ tenant: null, items: [] });
    const [memberOpen, setMemberOpen] = useState(false);
    const [memberDraft, setMemberDraft] = useState({ email: "", name: "", title: "" });
    const [memberMsg, setMemberMsg] = useState("");

    const load = async () => {
        try {
            setErr("");
            const [{ data: ts }, { data: ov }, integrationsRes] = await Promise.all([
                api.get("/tenants"),
                api.get("/platform/overview"),
                api.get("/platform/integrations").catch(() => ({ data: null })),
            ]);
            setTenants(ts);
            setOverview(ov);
            setIntegrations(integrationsRes.data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onCreate = async (draft) => {
        const payload = {
            ...draft,
            slug: draft.slug.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
            founded_year: draft.founded_year ? parseInt(draft.founded_year, 10) : null,
            commission_pct: parseInt(draft.commission_pct ?? 15, 10),
            pricing_monthly_gbp: draft.pricing_monthly_gbp ? parseInt(draft.pricing_monthly_gbp, 10) : 0,
            subdomain: draft.subdomain || `${draft.slug}.floodlight.club`,
            brand: { ...draft.brand, crest_initials: (draft.short_name || draft.name).slice(0, 2).toUpperCase() },
        };
        const { data: created } = await api.post("/tenants", payload);
        // If a logo file is attached, upload it now (we have the tenant id back)
        if (draft._logoFile) {
            const fd = new FormData();
            fd.append("file", draft._logoFile);
            await api.post(`/tenants/${created.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        setCreating(false);
        await load();
    };

    const onUpdate = async (id, patch, logoFile) => {
        if (Object.keys(patch).length > 0) {
            await api.patch(`/tenants/${id}`, patch);
        }
        if (logoFile) {
            const fd = new FormData();
            fd.append("file", logoFile);
            await api.post(`/tenants/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        setEditing(null);
        await load();
    };

    const onDelete = async (id) => {
        if (!window.confirm(`Delete tenant ${id}? This wipes their sponsors, packages and invoices.`)) return;
        await api.delete(`/tenants/${id}`);
        await load();
    };

    const onResetTenant = async (tenant) => {
        const ok = window.confirm(
            `Reset demo data for ${tenant.name}?\n\nThis wipes all sponsors, sponsor users, invoices, offers, and slot allocations, but keeps:\n  • the club + brand + crest\n  • the club admin accounts\n  • packages (so you can re-assign sponsors fast)\n\nUse before a live demo to show a clean onboarding flow.`
        );
        if (!ok) return;
        try {
            const { data } = await api.post(`/platform/tenants/${tenant.id}/reset`, { keep_packages: true, keep_members: true });
            const lines = Object.entries(data.summary).map(([k, v]) => `  ${k}: ${v}`).join("\n");
            alert(`Reset complete for ${data.tenant}.\n\n${lines}`);
            await load();
        } catch (e) {
            alert(`Reset failed: ${formatApiErrorDetail(e.response?.data?.detail) || e.message}`);
        }
    };

    const openMembers = async (tenant) => {
        const { data } = await api.get(`/tenants/${tenant.id}/members`);
        setMembers({ tenant, items: data });
        setMemberOpen(true);
        setMemberMsg("");
        setMemberDraft({ email: "", name: "", title: "" });
    };

    const onAddMember = async () => {
        try {
            setMemberMsg("");
            const { data } = await api.post(`/tenants/${members.tenant.id}/members`, memberDraft);
            setMemberMsg(`Sent. Login: ${data.email} / ${data._provisional_password}`);
            const refreshed = await api.get(`/tenants/${members.tenant.id}/members`);
            setMembers({ tenant: members.tenant, items: refreshed.data });
            setMemberDraft({ email: "", name: "", title: "" });
        } catch (e) {
            setMemberMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const onRemoveMember = async (uid) => {
        if (!window.confirm("Remove this member?")) return;
        await api.delete(`/tenants/${members.tenant.id}/members/${uid}`);
        const refreshed = await api.get(`/tenants/${members.tenant.id}/members`);
        setMembers({ tenant: members.tenant, items: refreshed.data });
    };

    const onViewAs = async (tenant) => {
        // Find a club_admin (or sponsor) belonging to this tenant and impersonate them.
        try {
            const { data } = await api.get("/platform/users");
            const candidates = (data.users || []).filter(
                (u) => u.tenant_id === tenant.id && u.role === "club_admin"
            );
            if (candidates.length === 0) {
                alert(`No club admins exist for ${tenant.name} yet. Invite one from the members panel first.`);
                return;
            }
            // Prefer the most recently created admin
            const target = candidates[0];
            const ok = window.confirm(
                `Sign in as ${target.name} (${target.email})?\n\nYou'll be redirected to ${tenant.name}'s dashboard. Sign out to return to Floodlight.`
            );
            if (!ok) return;
            await api.post(`/platform/impersonate/${target.id}`);
            // Force a hard reload so the AuthProvider re-fetches /auth/me and routes to /club
            window.location.href = "/club";
        } catch (e) {
            alert(`View-as failed: ${formatApiErrorDetail(e.response?.data?.detail) || e.message}`);
        }
    };

    return (
        <div data-testid="platform-page" className="min-h-screen text-white bg-[var(--sb-page)]">
            <div className="grid grid-cols-12 min-h-screen">
                {/* Sidebar */}
                <aside className="hidden lg:flex lg:col-span-2 flex-col border-r hairline sticky top-0 self-start h-screen">
                    <div className="p-6 border-b hairline">
                        <SouburWordmark product="Club" />
                        <div className="mt-4 text-[10px] uppercase tracking-ultra-wide text-zinc-500">Platform · god view</div>
                    </div>
                    <div className="flex-1 px-3 py-4 space-y-0.5 text-sm">
                        <div className="px-3 py-2.5 bg-white/5 text-white border-l-2 pl-[10px]" style={{ borderLeftColor: "var(--sb-amber)" }}>
                            <span className="inline-flex items-center gap-2.5"><Building2 size={15} strokeWidth={1.6} /> Clubs</span>
                        </div>
                        <div className="px-3 py-2.5 text-zinc-500"><span className="inline-flex items-center gap-2.5"><Sparkles size={15} strokeWidth={1.6} /> Onboarding queue<span className="text-[9px] uppercase tracking-ultra-wide text-zinc-600 ml-auto">Soon</span></span></div>
                    </div>
                    <div className="p-3 border-t hairline">
                        <div className="flex items-center gap-2.5 px-3 py-2.5">
                            <div className="w-7 h-7 bg-[var(--sb-amber)] text-black flex items-center justify-center text-xs font-bold">JW</div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm truncate">{user?.name}</div>
                                <div className="text-[10px] text-zinc-500 uppercase tracking-ultra-wide truncate">Platform owner</div>
                            </div>
                        </div>
                    </div>
                </aside>

                <main className="col-span-12 lg:col-span-10">
                    <TopBar
                        breadcrumb={<><span>Floodlight</span><span className="text-zinc-700 mx-2">/</span><span className="text-white">Clubs</span></>}
                        right={
                            <PrimaryButton onClick={() => setCreating(true)} data-testid="add-club-btn">
                                <Plus size={14} strokeWidth={2.4} /> Add club
                            </PrimaryButton>
                        }
                    />

                    <div className="px-6 md:px-10 py-10 space-y-10">
                        {/* Title */}
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Platform overview</div>
                                <h1 className="font-sport text-4xl md:text-5xl leading-none uppercase" data-testid="platform-title">
                                    Welcome, Josh.
                                </h1>
                                <p className="font-editorial text-zinc-400 mt-3 text-lg italic">
                                    {tenants.length === 0 ? "No clubs onboarded yet. Add the first one." : `${tenants.length} club${tenants.length === 1 ? "" : "s"} live. ${overview?.total_active_sponsors || 0} active sponsors across the network.`}
                                </p>
                            </div>
                        </div>

                        {/* KPIs */}
                        {overview && (
                            <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="platform-kpis">
                                <KpiCard label="Clubs onboarded" value={overview.tenants_count} testId="kpi-clubs" />
                                <KpiCard
                                    label="Your projected MRR"
                                    value={formatGBP(overview.platform_mrr_gbp)}
                                    sub={`${overview.commission_clubs} on commission · ${overview.flat_clubs} flat`}
                                    testId="kpi-platform-mrr"
                                    accent
                                />
                                <KpiCard label="Sponsors live" value={formatNumber(overview.total_active_sponsors)} sub="across all clubs" testId="kpi-total-sponsors" />
                                <ClubsGrossMrrCard overview={overview} />
                            </section>
                        )}

                        {/* Integrations status */}
                        {integrations && (
                            <section className="border hairline p-5" data-testid="integrations-status">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Integrations · read-only</div>
                                        <h2 className="font-display font-extrabold text-lg tracking-tight">Configured services</h2>
                                    </div>
                                    <div className="text-[11px] text-zinc-500 italic">Edit in your deployment env vars</div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                    <IntegrationCell
                                        label="Stripe"
                                        configured={integrations.stripe?.configured}
                                        mode={integrations.stripe?.key_mode}
                                        primary={integrations.stripe?.key_masked}
                                        secondary={integrations.stripe?.platform_account_id ? `acct · ${integrations.stripe.platform_account_id}` : "platform-implicit"}
                                        hint={integrations.stripe?.webhook_secret_configured ? "Webhook signed" : "No webhook secret"}
                                        testId="integration-stripe"
                                    />
                                    <IntegrationCell
                                        label="Resend"
                                        configured={integrations.resend?.configured}
                                        primary={integrations.resend?.sender || "—"}
                                        secondary={integrations.resend?.lead_recipient ? `leads → ${integrations.resend.lead_recipient}` : "no inbox set"}
                                        testId="integration-resend"
                                    />
                                    <IntegrationCell
                                        label="Firebase"
                                        configured={integrations.firebase?.configured}
                                        primary={integrations.firebase?.configured ? "Service account loaded" : "Not configured"}
                                        secondary="Firestore live analytics"
                                        testId="integration-firebase"
                                    />
                                    <IntegrationCell
                                        label="Platform owner"
                                        configured={!!integrations.platform_owner?.email}
                                        primary={integrations.platform_owner?.name || "—"}
                                        secondary={integrations.platform_owner?.email || "—"}
                                        testId="integration-owner"
                                    />
                                </div>
                            </section>
                        )}

                        {/* Clubs table */}
                        <section data-testid="clubs-section">
                            <div className="flex items-center justify-between mb-5">
                                <div>
                                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Tenants</div>
                                    <h2 className="font-display font-extrabold text-2xl tracking-tight">Clubs on Floodlight</h2>
                                </div>
                                <PrimaryButton onClick={() => setCreating(true)} data-testid="add-club-btn-2">
                                    <Plus size={14} strokeWidth={2.4} /> Add club
                                </PrimaryButton>
                            </div>

                            {loading ? (
                                <div className="border hairline p-12 text-center text-zinc-500 text-sm">Loading clubs…</div>
                            ) : err ? (
                                <div className="border border-rose-500/40 text-rose-300 p-6 text-sm">{err}</div>
                            ) : tenants.length === 0 ? (
                                <div className="border hairline p-12 text-center" data-testid="empty-clubs">
                                    <Building2 size={28} strokeWidth={1.4} className="text-zinc-500 mx-auto mb-4" />
                                    <h3 className="font-display font-bold text-xl tracking-tight">No clubs yet</h3>
                                    <p className="text-sm text-zinc-500 mt-2">Onboard your first club to see them here.</p>
                                    <div className="mt-6">
                                        <PrimaryButton onClick={() => setCreating(true)}>Add your first club</PrimaryButton>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                    {tenants.map((t) => (
                                        <div key={t.id} className="border hairline p-6 flex flex-col gap-5 hover:border-white/20 transition-colors" data-testid={`club-card-${t.id}`}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-3">
                                                    <Crest tenant={t} size={44} />
                                                    <div>
                                                        <div className="font-display font-extrabold text-lg tracking-tight">{t.name}</div>
                                                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{t.league || "League TBD"}</div>
                                                    </div>
                                                </div>
                                                <Pill tone="active" testId={`club-status-${t.id}`}>Live</Pill>
                                            </div>
                                            {t.brand?.tagline && <div className="font-editorial italic text-zinc-300 text-sm">"{t.brand.tagline}"</div>}
                                            <div className="grid grid-cols-3 gap-2 text-center">
                                                <div className="border-t hairline pt-3">
                                                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Sponsors</div>
                                                    <div className="font-display font-extrabold text-2xl tracking-tightest mt-1">{t.active_sponsors}</div>
                                                </div>
                                                <div className="border-t hairline pt-3 relative group">
                                                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Their MRR <span className="text-zinc-600">· net</span></div>
                                                    <div className="font-display font-extrabold text-2xl tracking-tightest mt-1">{formatGBP(t.club_net_gbp ?? Math.max(0, (t.total_mrr_gbp ?? t.mrr_gbp) - (t.soubor_take_gbp || 0)))}</div>
                                                    <div className="text-[10px] text-zinc-500 mt-0.5">
                                                        <span title="Sponsorship MRR (gross)">spon {formatGBP(t.mrr_gbp)}</span>
                                                        <span className="mx-1">·</span>
                                                        <span title="Fan-subscription MRR (gross)" style={{ color: t.brand?.primary || "var(--sb-amber)" }}>fans {formatGBP(t.fan_mrr_gbp || 0)}</span>
                                                        <span className="mx-1">·</span>
                                                        <span title="Floodlight's share">−{formatGBP(t.soubor_take_gbp || 0)}</span>
                                                    </div>
                                                </div>
                                                <div className="border-t hairline pt-3">
                                                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">
                                                        {t.pricing_mode === "commission" ? `Your ${t.commission_pct ?? 15}%` : "Pays you"}
                                                    </div>
                                                    <div className="font-display font-extrabold text-2xl tracking-tightest mt-1 text-[var(--sb-amber)]">{formatGBP(t.soubor_take_gbp)}</div>
                                                </div>
                                            </div>
                                            <div className="text-[11px] text-zinc-500 font-mono truncate">{t.subdomain}</div>
                                            <div className="flex items-center gap-2 pt-2 border-t hairline">
                                                <button onClick={() => openMembers(t)} className="text-xs text-zinc-300 hover:text-white inline-flex items-center gap-1.5 px-2 py-1.5" data-testid={`club-members-${t.id}`}>
                                                    <Users size={13} /> {t.admin_count || 0} member{t.admin_count === 1 ? "" : "s"}
                                                </button>
                                                <button onClick={() => setEditing(t)} className="text-xs text-zinc-300 hover:text-white inline-flex items-center gap-1.5 px-2 py-1.5" data-testid={`club-edit-${t.id}`}>
                                                    <Edit3 size={13} /> Edit
                                                </button>
                                                <button
                                                    onClick={() => onViewAs(t)}
                                                    className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1.5 ml-auto hover:brightness-110 transition-all"
                                                    style={{ background: t.brand?.primary || "var(--sb-amber)", color: t.brand?.primary_foreground || "#000" }}
                                                    data-testid={`club-view-as-${t.id}`}
                                                    title="Sign in as this club's admin"
                                                >
                                                    <Eye size={12} /> View as club
                                                </button>
                                                <button onClick={() => onResetTenant(t)} className="text-xs text-zinc-400 hover:text-white p-1.5" data-testid={`club-reset-${t.id}`} aria-label="Reset demo data" title="Wipe demo data, keep club + brand">
                                                    <Sparkles size={13} />
                                                </button>
                                                <button onClick={() => setDiagnosticOpen(t)} className="text-xs text-zinc-400 hover:text-white p-1.5" data-testid={`club-diagnostic-${t.id}`} aria-label="Stripe diagnostic" title="Stripe diagnostic — what Stripe is returning for this club">
                                                    <Stethoscope size={13} />
                                                </button>
                                                <button onClick={() => onDelete(t.id)} className="text-xs text-rose-400 hover:text-rose-300 p-1.5" data-testid={`club-delete-${t.id}`} aria-label="Delete">
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>

                        <FooterStrip />
                    </div>
                </main>
            </div>

            {creating && <ClubFormModal onClose={() => setCreating(false)} onSubmit={onCreate} />}
            {editing && <ClubFormModal initial={editing} editing onClose={() => setEditing(null)} onSubmit={(d, file) => onUpdate(editing.id, d, file)} />}
            {diagnosticOpen && <DiagnosticModal tenant={diagnosticOpen} onClose={() => setDiagnosticOpen(null)} />}

            {memberOpen && (
                <Modal open onClose={() => setMemberOpen(false)} title={`Head office · ${members.tenant?.name}`} testId="members-modal" wide>
                    <div className="space-y-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Existing members</div>
                            {members.items.length === 0 ? (
                                <div className="text-sm text-zinc-500 border hairline px-4 py-6 text-center">No head-office members yet. Invite one below.</div>
                            ) : (
                                <div className="space-y-2">
                                    {members.items.map((m) => (
                                        <div key={m.id} className="border hairline px-4 py-3 flex items-center justify-between" data-testid={`member-${m.id}`}>
                                            <div>
                                                <div className="text-sm text-white">{m.name}</div>
                                                <div className="text-[11px] text-zinc-500 font-mono">{m.email}</div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{m.title || m.role.replace("_", " ")}</span>
                                                <button onClick={() => onRemoveMember(m.id)} className="text-rose-400 hover:text-rose-300 p-1" aria-label="Remove member" data-testid={`member-remove-${m.id}`}>
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="border-t hairline pt-6">
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Invite a member</div>
                            <div className="grid grid-cols-12 gap-3">
                                <Field label="Full name" span={6}>
                                    <TextInput value={memberDraft.name} onChange={(v) => setMemberDraft({ ...memberDraft, name: v })} placeholder="Ian Brown" testId="member-name" />
                                </Field>
                                <Field label="Email" span={6}>
                                    <TextInput type="email" value={memberDraft.email} onChange={(v) => setMemberDraft({ ...memberDraft, email: v })} placeholder="ian@runcornlinnets.com" testId="member-email" />
                                </Field>
                                <Field label="Title" span={12}>
                                    <TextInput value={memberDraft.title} onChange={(v) => setMemberDraft({ ...memberDraft, title: v })} placeholder="Commercial Director" testId="member-title" />
                                </Field>
                            </div>
                            {memberMsg && <div className="mt-4 text-xs border border-emerald-500/40 text-emerald-300 px-3 py-2.5">{memberMsg}</div>}
                            <div className="mt-5 flex items-center justify-end gap-2">
                                <GhostButton onClick={() => setMemberOpen(false)}>Done</GhostButton>
                                <PrimaryButton onClick={onAddMember} disabled={!memberDraft.email || !memberDraft.name} data-testid="member-add-submit">
                                    <Plus size={14} strokeWidth={2.4} /> Send invite
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

const ClubFormModal = ({ initial, editing = false, onClose, onSubmit }) => {
    const [draft, setDraft] = useState(() => initial ? { ...emptyClub(), ...initial, brand: { ...emptyClub().brand, ...(initial.brand || {}) } } : emptyClub());
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    const [removeLogo, setRemoveLogo] = useState(false);
    const fileInputRef = useRef(null);
    const apiBase = process.env.REACT_APP_BACKEND_URL;

    const setBrand = (patch) => setDraft((d) => ({ ...d, brand: { ...d.brand, ...patch } }));

    const onFile = (f) => {
        if (!f) return;
        if (!["image/png", "image/jpeg", "image/webp", "image/svg+xml"].includes(f.type)) {
            setErr("Logo must be PNG, JPG, WebP or SVG");
            return;
        }
        if (f.size > 2 * 1024 * 1024) {
            setErr("Logo too large (2MB max)");
            return;
        }
        setErr("");
        setLogoFile(f);
        setRemoveLogo(false);
        const url = URL.createObjectURL(f);
        setLogoPreviewUrl(url);
    };

    const onRemoveLogo = () => {
        setLogoFile(null);
        setLogoPreviewUrl(null);
        setRemoveLogo(true);
    };

    const submit = async () => {
        try {
            setErr(""); setBusy(true);
            // If user clicked Remove on an existing logo, clear server-side first
            if (editing && removeLogo && initial?.brand?.logo_path) {
                try { await api.delete(`/tenants/${initial.id}/logo`); } catch { /* ignore */ }
            }
            if (editing) {
                const patch = {
                    name: draft.name,
                    short_name: draft.short_name,
                    league: draft.league,
                    venue: draft.venue,
                    founded_year: draft.founded_year ? parseInt(draft.founded_year, 10) : null,
                    season: draft.season,
                    subdomain: draft.subdomain,
                    pricing_mode: draft.pricing_mode,
                    commission_pct: parseInt(draft.commission_pct ?? 15, 10),
                    transaction_fees_absorbed: !!draft.transaction_fees_absorbed,
                    pricing_monthly_gbp: draft.pricing_monthly_gbp ? parseInt(draft.pricing_monthly_gbp, 10) : 0,
                    stripe_account_id: draft.stripe_account_id || null,
                    stripe_metadata_key: draft.stripe_metadata_key || null,
                    stripe_metadata_value: draft.stripe_metadata_value || null,
                    stripe_metadata_tier_key: draft.stripe_metadata_tier_key || null,
                    payout_schedule: draft.payout_schedule || null,
                    subscriber_tiers: draft.subscriber_tiers || [],
                    show_on_landing: draft.show_on_landing !== false,
                    brand: { ...draft.brand, crest_initials: (draft.short_name || draft.name).slice(0, 2).toUpperCase() },
                };
                await onSubmit(patch, logoFile);
            } else {
                await onSubmit({ ...draft, _logoFile: logoFile });
            }
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const previewInitials = (draft.short_name || draft.name || "FC").slice(0, 2).toUpperCase();
    const existingLogoUrl = !removeLogo && initial?.brand?.logo_path
        ? `${apiBase}/api/files/${initial.brand.logo_path}`
        : null;
    const previewLogo = logoPreviewUrl || existingLogoUrl;

    return (
        <Modal open onClose={onClose} wide testId="club-form-modal" title={editing ? "Edit club" : "Onboard a new club"}
            footer={<>
                <GhostButton onClick={onClose}>Cancel</GhostButton>
                <PrimaryButton onClick={submit} disabled={busy || !draft.name || !draft.slug} data-testid="club-form-submit">
                    {busy ? "Saving…" : editing ? "Save changes" : "Create tenant"}
                </PrimaryButton>
            </>}>
            <div className="grid grid-cols-12 gap-4">
                <Field label="Slug (subdomain)" span={4} hint={`Will be ${draft.slug || "slug"}.floodlight.club`}>
                    <TextInput value={draft.slug} onChange={(v) => setDraft({ ...draft, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="rlfc" testId="club-form-slug" disabled={editing} />
                </Field>
                <Field label="Club name" span={8}>
                    <TextInput value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="Runcorn Linnets FC" testId="club-form-name" />
                </Field>
                <Field label="Short name" span={4}>
                    <TextInput value={draft.short_name} onChange={(v) => setDraft({ ...draft, short_name: v })} placeholder="Linnets" testId="club-form-short" />
                </Field>
                <Field label="League" span={8}>
                    <TextInput value={draft.league} onChange={(v) => setDraft({ ...draft, league: v })} placeholder="Northern Premier League — Premier" testId="club-form-league" />
                </Field>
                <Field label="Venue" span={6}>
                    <TextInput value={draft.venue} onChange={(v) => setDraft({ ...draft, venue: v })} placeholder="Millbank Linnets Stadium" testId="club-form-venue" />
                </Field>
                <Field label="Founded" span={3}>
                    <TextInput type="number" value={draft.founded_year} onChange={(v) => setDraft({ ...draft, founded_year: v })} placeholder="1937" testId="club-form-founded" />
                </Field>
                <Field label="Season" span={3}>
                    <TextInput value={draft.season} onChange={(v) => setDraft({ ...draft, season: v })} placeholder="2025/26" testId="club-form-season" />
                </Field>

                <div className="col-span-12 border-t hairline pt-5 mt-1">
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3 inline-flex items-center gap-2"><Palette size={12} /> White-label</div>
                </div>

                {/* LOGO UPLOAD */}
                <div className="col-span-12 md:col-span-4">
                    <label className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 block mb-2">Club crest</label>
                    <div className="border hairline aspect-square flex items-center justify-center relative overflow-hidden" data-testid="logo-uploader">
                        {previewLogo ? (
                            <img src={previewLogo} alt="logo preview" className="w-full h-full object-contain bg-[#0a0a0a]" />
                        ) : (
                            <div className="flex items-center justify-center w-full h-full font-display font-extrabold tracking-tight" style={{ background: draft.brand.primary, color: draft.brand.primary_foreground, fontSize: 56 }}>
                                {previewInitials}
                            </div>
                        )}
                        {previewLogo && (
                            <button type="button" onClick={onRemoveLogo} className="absolute top-2 right-2 bg-black/80 border hairline p-1.5 hover:bg-black" aria-label="Remove logo" data-testid="logo-remove">
                                <XIcon size={12} className="text-white" />
                            </button>
                        )}
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="logo-file-input" />
                    <div className="mt-2 flex gap-2">
                        <button type="button" onClick={() => fileInputRef.current?.click()}
                            className="flex-1 inline-flex items-center justify-center gap-2 border hairline text-xs px-3 py-2 hover:bg-white/5"
                            data-testid="logo-pick">
                            <Upload size={12} /> {previewLogo ? "Replace" : "Upload"}
                        </button>
                    </div>
                    <div className="text-[10px] text-zinc-600 mt-1.5">PNG, JPG, WebP or SVG · 2MB max · square works best</div>
                </div>

                <div className="col-span-12 md:col-span-8 grid grid-cols-12 gap-4 content-start">
                    <Field label="Tagline" span={12} hint="Shows on club admin dashboard + sponsor portal">
                        <TextInput value={draft.brand.tagline} onChange={(v) => setBrand({ tagline: v })} placeholder="The Yellow & Green of the Northern Premier League." testId="club-form-tagline" />
                    </Field>

                    <div className="col-span-12">
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">Colour preset</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {COLOR_PRESETS.map((p) => (
                                <button key={p.name} type="button" onClick={() => setBrand({ primary: p.primary, primary_foreground: p.primary_foreground })}
                                    className={`flex items-center gap-3 border px-3 py-2 text-left transition-colors ${draft.brand.primary === p.primary ? "border-white/40" : "hairline hover:border-white/20"}`}
                                    data-testid={`preset-${p.name.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                                    <span className="w-5 h-5 inline-block" style={{ background: p.primary }} />
                                    <span className="text-xs">{p.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <Field label="Primary hex" span={6}>
                        <TextInput value={draft.brand.primary} onChange={(v) => setBrand({ primary: v })} placeholder="#166534" testId="club-form-primary" />
                    </Field>
                    <Field label="Primary foreground" span={6}>
                        <TextInput value={draft.brand.primary_foreground} onChange={(v) => setBrand({ primary_foreground: v })} placeholder="#FEF08A" testId="club-form-primary-fg" />
                    </Field>
                </div>

                {/* PRICING */}
                <div className="col-span-12 border-t hairline pt-5 mt-1">
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3 inline-flex items-center gap-2"><Percent size={12} /> Commercial terms</div>
                </div>

                <div className="col-span-12 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <button type="button" onClick={() => setDraft({ ...draft, pricing_mode: "commission" })}
                        className={`text-left border px-4 py-4 transition-colors ${draft.pricing_mode === "commission" ? "border-white/40 bg-white/[0.03]" : "hairline hover:border-white/20"}`}
                        data-testid="pricing-mode-commission">
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Commission</div>
                        <div className="font-display font-extrabold text-2xl tracking-tight mt-1">15%<span className="text-zinc-500 text-sm font-medium"> of sponsorship MRR</span></div>
                        <div className="text-xs text-zinc-400 mt-2">"You only pay when you get paid." Stripe fees absorbed by Floodlight.</div>
                    </button>
                    <button type="button" onClick={() => setDraft({ ...draft, pricing_mode: "flat" })}
                        className={`text-left border px-4 py-4 transition-colors ${draft.pricing_mode === "flat" ? "border-white/40 bg-white/[0.03]" : "hairline hover:border-white/20"}`}
                        data-testid="pricing-mode-flat">
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Flat fee</div>
                        <div className="font-display font-extrabold text-2xl tracking-tight mt-1">£<span data-testid="flat-amount">{draft.pricing_monthly_gbp || 0}</span><span className="text-zinc-500 text-sm font-medium"> /mo</span></div>
                        <div className="text-xs text-zinc-400 mt-2">Fixed monthly subscription. For bigger clubs that prefer predictability.</div>
                    </button>
                </div>

                {draft.pricing_mode === "commission" ? (
                    <>
                        <Field label="Commission %" span={3}>
                            <TextInput type="number" value={draft.commission_pct} onChange={(v) => setDraft({ ...draft, commission_pct: v })} placeholder="15" testId="club-form-commission" />
                        </Field>
                        <Field label="Floodlight absorbs Stripe fees" span={9}>
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!draft.transaction_fees_absorbed} onChange={(e) => setDraft({ ...draft, transaction_fees_absorbed: e.target.checked })} data-testid="club-form-fees-absorbed" />
                                <span>Yes — the club's invoices show no platform deductions or surcharges</span>
                            </label>
                        </Field>
                    </>
                ) : (
                    <Field label="Monthly fee (GBP)" span={4}>
                        <TextInput type="number" value={draft.pricing_monthly_gbp} onChange={(v) => setDraft({ ...draft, pricing_monthly_gbp: v })} placeholder="99" testId="club-form-pricing" />
                    </Field>
                )}

                <Field label="Stripe Connect account ID" span={12} hint="acct_... — when set, we query this connected account directly. Takes precedence over metadata fields below.">
                    <TextInput value={draft.stripe_account_id || ""} onChange={(v) => setDraft({ ...draft, stripe_account_id: v.trim() })} placeholder="acct_1RJeMeHGYJsD3uC8" testId="club-form-stripe-account" />
                </Field>

                <Field label="Stripe metadata key" span={4} hint="Only used when the Connect account ID above is blank. Tag your platform's subs with this key…">
                    <TextInput value={draft.stripe_metadata_key || ""} onChange={(v) => setDraft({ ...draft, stripe_metadata_key: v.trim() })} placeholder="club" testId="club-form-stripe-meta-key" />
                </Field>
                <Field label="Stripe metadata value" span={8} hint="…and this value. e.g. metadata.club = RLFC.">
                    <TextInput value={draft.stripe_metadata_value || ""} onChange={(v) => setDraft({ ...draft, stripe_metadata_value: v.trim() })} placeholder="RLFC" testId="club-form-stripe-meta-value" />
                </Field>

                <Field label="Payout schedule" span={12} hint="Free-text note describing this club's Stripe payout cadence. Shown to the club next to their Available balance so they know when funds clear.">
                    <TextInput value={draft.payout_schedule || ""} onChange={(v) => setDraft({ ...draft, payout_schedule: v })} placeholder="Daily · T+7 day rolling hold" testId="club-form-payout-schedule" />
                </Field>

                <Field label="Public visibility" span={12} hint="Show this club in the 'Trusted by' strip on floodlight.club homepage. Untick to keep them private.">
                    <label className="inline-flex items-center gap-3 text-sm cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={draft.show_on_landing !== false}
                            onChange={(e) => setDraft({ ...draft, show_on_landing: e.target.checked })}
                            data-testid="club-form-show-on-landing"
                        />
                        <span>List this club on the public Floodlight landing page</span>
                    </label>
                </Field>

                <div className="col-span-12 border-t hairline pt-5">
                    <Field label="Tier metadata key" span={12} hint="Stripe subscription metadata field that identifies which tier each fan is on (e.g. 'tier'). Each tier below matches subs where metadata[tierKey] equals its metadata value. If unset or unmatched, we fall back to monthly amount.">
                        <TextInput value={draft.stripe_metadata_tier_key || ""} onChange={(v) => setDraft({ ...draft, stripe_metadata_tier_key: v.trim() })} placeholder="tier" testId="club-form-tier-key" />
                    </Field>
                    <TierEditor
                        tiers={draft.subscriber_tiers || []}
                        onChange={(tiers) => setDraft({ ...draft, subscriber_tiers: tiers })}
                        tenantId={initial?.id}
                        apiBase={apiBase}
                    />
                </div>

                <div className="col-span-12 border-t hairline pt-5 mt-1 flex items-center gap-4">
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Live preview</div>
                    <div className="flex items-center gap-3">
                        {previewLogo ? (
                            <div className="w-10 h-10 border hairline overflow-hidden bg-[#0a0a0a]">
                                <img src={previewLogo} alt="" className="w-full h-full object-contain" />
                            </div>
                        ) : (
                            <div className="w-10 h-10 flex items-center justify-center font-display font-extrabold text-sm" style={{ background: draft.brand.primary, color: draft.brand.primary_foreground }}>{previewInitials}</div>
                        )}
                        <div>
                            <div className="font-display font-extrabold tracking-tight">{draft.name || "Your club"}</div>
                            <div className="text-[10px] uppercase tracking-ultra-wide" style={{ color: draft.brand.primary_foreground }}>
                                <span className="px-1.5 py-0.5" style={{ background: draft.brand.primary }}>tenant primary</span>
                            </div>
                        </div>
                    </div>
                </div>

                {err && <div className="col-span-12 text-xs border border-rose-500/40 text-rose-300 px-3 py-2.5">{err}</div>}
            </div>
        </Modal>
    );
};


/* ------------ Tier editor (subscriber_tiers CRUD) ------------ */
const newTierId = () => `tier-${Math.random().toString(36).slice(2, 9)}`;

const TierEditor = ({ tiers, onChange, tenantId, apiBase }) => {
    const [uploading, setUploading] = useState(null); // tier id while icon is uploading

    const add = () => onChange([...(tiers || []), { id: newTierId(), name: "", metadata_value: "", monthly_amount_gbp: null, icon_path: null, sort_order: (tiers?.length || 0) }]);
    const update = (id, patch) => onChange(tiers.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    const remove = (id) => onChange(tiers.filter((t) => t.id !== id));

    const uploadIcon = async (tier, file) => {
        if (!file || !tenantId) return;
        setUploading(tier.id);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/tenants/${tenantId}/tier-icon/${tier.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            update(tier.id, { icon_path: data.path });
        } catch (e) {
            alert(`Icon upload failed: ${formatApiErrorDetail(e.response?.data?.detail) || e.message}`);
        } finally {
            setUploading(null);
        }
    };

    return (
        <div data-testid="tier-editor">
            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">Subscriber tiers</div>
            <div className="space-y-2">
                {(tiers || []).length === 0 && <div className="text-[11px] italic text-zinc-500">No tiers yet — fans will appear in the table without a tier badge.</div>}
                {(tiers || []).map((t) => (
                    <div key={t.id} className="grid grid-cols-12 gap-2 items-center border hairline p-3" data-testid={`tier-row-${t.id}`}>
                        <div className="col-span-12 md:col-span-1">
                            {t.icon_path ? (
                                <img src={`${apiBase}/api/files/${t.icon_path}`} alt="" className="w-10 h-10 object-contain border hairline bg-[#050505] p-1" />
                            ) : (
                                <div className="w-10 h-10 flex items-center justify-center text-[10px] font-bold border hairline text-zinc-500 bg-[#050505]">no icon</div>
                            )}
                        </div>
                        <div className="col-span-6 md:col-span-3">
                            <div className="text-[9px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Display name</div>
                            <input value={t.name || ""} onChange={(e) => update(t.id, { name: e.target.value })} placeholder="Linnet Legend"
                                className="w-full bg-[#060b07] border hairline px-2 py-1.5 text-xs text-white outline-none focus:border-white/30"
                                data-testid={`tier-${t.id}-name`} />
                        </div>
                        <div className="col-span-6 md:col-span-3">
                            <div className="text-[9px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Metadata value</div>
                            <input value={t.metadata_value || ""} onChange={(e) => update(t.id, { metadata_value: e.target.value })} placeholder="£10.00"
                                className="w-full bg-[#060b07] border hairline px-2 py-1.5 text-xs text-white outline-none focus:border-white/30"
                                data-testid={`tier-${t.id}-meta`} />
                        </div>
                        <div className="col-span-6 md:col-span-2">
                            <div className="text-[9px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Monthly (£)</div>
                            <input type="number" step="0.01" value={t.monthly_amount_gbp ?? ""} onChange={(e) => update(t.id, { monthly_amount_gbp: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="10.00"
                                className="w-full bg-[#060b07] border hairline px-2 py-1.5 text-xs text-white outline-none focus:border-white/30"
                                data-testid={`tier-${t.id}-amount`} />
                        </div>
                        <div className="col-span-10 md:col-span-2 flex items-center gap-2">
                            <label className="inline-flex items-center gap-1 text-[10px] uppercase tracking-ultra-wide border hairline px-2 py-1.5 cursor-pointer hover:border-white/30">
                                <Upload size={11} />
                                {uploading === t.id ? "…" : "Icon"}
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadIcon(t, e.target.files?.[0])} disabled={!tenantId || uploading === t.id} />
                            </label>
                        </div>
                        <button type="button" onClick={() => remove(t.id)} className="col-span-2 md:col-span-1 text-rose-400 hover:text-rose-300 p-1.5 inline-flex items-center justify-center" data-testid={`tier-${t.id}-remove`}>
                            <Trash2 size={12} />
                        </button>
                    </div>
                ))}
            </div>
            <button type="button" onClick={add} className="mt-2 text-[10px] uppercase tracking-ultra-wide border hairline px-3 py-1.5 text-zinc-300 hover:border-white/30 inline-flex items-center gap-1.5" data-testid="tier-editor-add">
                <Plus size={11} /> Add tier
            </button>
            {!tenantId && <div className="text-[10px] text-zinc-500 italic mt-1">Save the club first, then you'll be able to upload tier icons.</div>}
        </div>
    );
};



const ClubsGrossMrrCard = ({ overview }) => {
    const spon = Number(overview?.total_sponsorship_mrr_gbp || 0);
    const fans = Number(overview?.total_fan_mrr_gbp || 0);
    const gross = Number(overview?.total_clubs_gross_mrr_gbp || spon + fans);
    const platformTake = Number(overview?.platform_mrr_gbp || 0);
    // What the clubs collectively NET after Floodlight's cut.
    const clubsNet = Math.max(0, gross - platformTake);
    return (
        <div className="border hairline p-5" data-testid="kpi-clubs-gross-mrr">
            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Clubs' net MRR <span className="text-zinc-600">· post-fee</span></div>
            <div className="font-display font-extrabold text-3xl md:text-4xl tracking-tightest mt-2">{formatGBP(clubsNet)}</div>
            <div className="text-[10px] mt-2 leading-relaxed text-zinc-500">
                {formatGBP(gross)} gross · −{formatGBP(platformTake)} fee
                <span className="block">
                    Sponsorship <span className="text-zinc-300">{formatGBP(spon)}</span>
                    <span className="mx-1">+</span>
                    Fans <span className="text-[var(--sb-amber)]">{formatGBP(fans)}</span>
                </span>
            </div>
        </div>
    );
};


const IntegrationCell = ({ label, configured, mode, primary, secondary, hint, testId }) => (
    <div className="border hairline p-4" data-testid={testId}>
        <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{label}</div>
            <span className={`text-[10px] uppercase tracking-ultra-wide px-1.5 py-0.5 ${configured ? "text-emerald-300 bg-emerald-500/10" : "text-zinc-500 bg-zinc-500/10"}`}>
                {configured ? "connected" : "not set"}
            </span>
        </div>
        {mode && (
            <div className="text-[9px] uppercase tracking-ultra-wide mt-2" style={{ color: mode === "live" ? "#fda4af" : "#fcd34d" }}>
                {mode} mode
            </div>
        )}
        <div className="font-display font-bold text-sm tracking-tight mt-2 truncate" title={primary}>{primary}</div>
        {secondary && <div className="text-[11px] text-zinc-500 truncate font-mono mt-1" title={secondary}>{secondary}</div>}
        {hint && <div className="text-[10px] text-zinc-500 italic mt-1.5">{hint}</div>}
    </div>
);


/* ------------ Stripe diagnostic modal (platform-owner only) ------------ */
const DiagnosticModal = ({ tenant, onClose }) => {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/stripe/diagnostic?tenant_id=${tenant.id}`);
                if (!cancelled) setData(r.data);
            } catch (e) {
                if (!cancelled) setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [tenant.id]);

    return (
        <Modal open onClose={onClose} title={`Stripe diagnostic · ${tenant.name}`} testId="platform-diagnostic-modal" wide>
            <div className="space-y-5 text-sm">
                {loading && <div className="text-zinc-400">Querying Stripe…</div>}
                {err && <div className="border border-rose-500/40 text-rose-300 p-4 text-xs">{err}</div>}
                {data && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <DiagRow label="Key mode" value={data.stripe_key_mode} />
                            <DiagRow label="Key (masked)" value={data.stripe_key_masked} mono />
                            <DiagRow label="Connect acct (tenant)" value={data.stripe_account_id_on_tenant || "(none)"} mono />
                            <DiagRow label="Metadata filter" value={`${data.stripe_metadata_filter.key || "?"} = ${data.stripe_metadata_filter.value || "?"}`} mono />
                            <DiagRow label="Tier metadata key" value={data.stripe_metadata_tier_key || "(not set)"} mono />
                            <DiagRow label="Tiers configured" value={String(data.subscriber_tiers_configured)} />
                            <DiagRow label="Sample size pulled" value={String(data.platform_subscriptions_sampled)} />
                            <DiagRow label="…matching filter in sample" value={String(data.platform_subscriptions_matching_filter ?? "—")} />
                        </div>
                        <div className="text-[11px] text-zinc-500 italic">{data.hint}</div>
                        {data.sample && data.sample.length > 0 && (
                            <div>
                                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">
                                    Sample (most recent on platform — first 10).
                                    Each entry shows the <span className="text-amber-300">merged_metadata</span> (subscription + item + price + product + customer) and <span className="text-amber-300">customer_metadata</span> separately, so you can spot exactly where your tags live.
                                </div>
                                <pre className="bg-[#050505] border hairline text-[11px] text-zinc-300 p-3 overflow-x-auto font-mono leading-relaxed max-h-96">{JSON.stringify(data.sample, null, 2)}</pre>
                            </div>
                        )}
                        {data.platform_subscriptions_sampled === 0 && (
                            <div className="text-xs text-amber-300 border border-amber-500/30 px-3 py-2">
                                Stripe returned <strong>zero</strong> subscriptions on this key. Either there are no subscriptions yet, or the key is targeting the wrong account.
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

const DiagRow = ({ label, value, mono }) => (
    <div className="border-t hairline pt-2">
        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{label}</div>
        <div className={`mt-0.5 text-white ${mono ? "font-mono text-[11px] break-all" : "text-sm"}`}>{value}</div>
    </div>
);
